import type { EventOptions } from '@amplitude/analytics-types';
import {
  createContext,
  memo,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react';

import { useJWT } from '@doger/core/modules/auth';
import { CookieBanner } from '@doger/core/modules/cookie-banner/cookie-banner.component';
import type { CookiePreferences } from '@doger/core/shared/cookies';

import type { Events } from './amplitude.events';
import { AmplitudeService } from './amplitude.service';
import { ClarityService } from './clarity.service';
import { GtagService } from './gtag.service';

type AnalyticsProviderProps = {
  children: ReactNode;
};

const AnalyticsContext = createContext<{
  amplitude: AmplitudeService<Events>;
  clarity: ClarityService;
} | null>(null);

export const AnalyticsProvider = ({ children }: AnalyticsProviderProps) => {
  const [clarity] = useState(() => new ClarityService());
  const [amplitude] = useState(() => new AmplitudeService<Events>());

  const jwt = useJWT();

  useLayoutEffect(() => {
    amplitude.identify(jwt ? jwt.id : null);
  }, [jwt?.id]);

  return (
    <AnalyticsContext.Provider
      value={useMemo(() => ({ amplitude, clarity }), [amplitude, clarity])}
    >
      {children}
    </AnalyticsContext.Provider>
  );
};

export const useAnalytics = () => {
  const context = useContext(AnalyticsContext);

  if (context === null) {
    throw new Error('useAnalytics must be used within an AnalyticsProvider');
  }

  return context;
};

export const AppCookieBanner = memo(() => {
  const { onPreferences } = useCookieConsent();

  return <CookieBanner onPreferences={onPreferences} />;
});

export const useCookieConsent = () => {
  const { amplitude, clarity } = useAnalytics();

  return {
    onPreferences: useCallback(
      (preferences: CookiePreferences) => {
        amplitude.consent(preferences.settings.analytics);
        clarity.consent(preferences.settings.analytics);
        GtagService.consent(preferences.settings);
      },
      [amplitude, clarity]
    ),
  };
};

export type LogAmplitudeEventOnMountProps<
  T extends {
    event: string;
    properties?: Record<string, unknown>;
  },
> = T & {
  options?: EventOptions;
};

/**
 * (!!!)
 * Doesn't handle case with changing props
 * to not log new event on every render or
 * force users to use memoization for props.
 * Use `key` prop to force remount component and log event again
 */
export const LogAmplitudeEventOnMount = (
  event: LogAmplitudeEventOnMountProps<Events>
) => {
  const analytics = useAnalytics();

  useEffect(() => {
    analytics.amplitude.track(event);
  }, []);

  return null;
};
